import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { BiTrash } from "react-icons/bi";
import axiosClient from "../utils/apiClient.js";
import { API_BASE_URL } from "../utils/urls.js";

const TimeEntryList = () => {
  const { caseId } = useParams();
  const [timeEntries, setTimeEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [isDeleting, setIsDeleting] = useState(false);

  const user = JSON.parse(localStorage.getItem("user"));
  const isAdmin = user?.roleName === "admin";
  const isCaseManager = user?.roleName === "case_manager";
  const canDelete = isAdmin || isCaseManager;

  useEffect(() => {
    const fetchTimeEntries = async () => {
      const token = localStorage.getItem("token");
      if (!token) {
        setError("Authentication token not found.");
        setLoading(false);
        return;
      }
      try {
        const response = await axiosClient.get(
          `${API_BASE_URL}/time-entries/case/${caseId}`
        );
        setTimeEntries(response.data);
      } catch (err) {
        console.error("Error fetching time entries:", err);
        setError(
          err.response?.data?.message ||
            "Failed to load time entries. Please try again."
        );
      } finally {
        setLoading(false);
      }
    };

    if (caseId) {
      fetchTimeEntries();
    } else {
      setError("No case ID provided.");
      setLoading(false);
    }
  }, [caseId]);

  // Filter time entries based on the search query
  const filteredTimeEntries = timeEntries.filter(
    (entry) =>
      (entry.description || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
      (`${entry.first_name || ""} ${entry.last_name || ""}`)
        .toLowerCase()
        .includes(searchQuery.toLowerCase())
  );

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedIds(filteredTimeEntries.map((item) => item.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleToggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleBulkDelete = async () => {
    if (!selectedIds.length) return;
    if (
      !window.confirm(
        `Are you sure you want to delete the ${selectedIds.length} selected time entry/entries?`
      )
    ) {
      return;
    }

    setIsDeleting(true);
    setError(null);
    try {
      await axiosClient.post(`${API_BASE_URL}/time-entries/bulk-delete`, {
        ids: selectedIds,
      });
      setTimeEntries((prev) => prev.filter((entry) => !selectedIds.includes(entry.id)));
      setSelectedIds([]);
    } catch (err) {
      console.error("Bulk delete error, attempting fallback:", err);
      try {
        await Promise.all(
          selectedIds.map((id) =>
            axiosClient.delete(`${API_BASE_URL}/time-entries/${id}`)
          )
        );
        setTimeEntries((prev) => prev.filter((entry) => !selectedIds.includes(entry.id)));
        setSelectedIds([]);
      } catch (fallbackErr) {
        setError(
          err.response?.data?.message || "Failed to delete selected time entries."
        );
      }
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDelete = async (entryId) => {
    if (window.confirm("Are you sure you want to delete this time entry?")) {
      try {
        await axiosClient.delete(`${API_BASE_URL}/time-entries/${entryId}`);
        setTimeEntries((prev) => prev.filter((entry) => entry.id !== entryId));
        setSelectedIds((prev) => prev.filter((id) => id !== entryId));
      } catch (err) {
        console.error("Delete failed", err);
        setError("Failed to delete time entry.");
      }
    }
  };

  if (loading)
    return <div className="p-6 text-center">Loading time entries...</div>;
  if (error) return <div className="p-6 text-center text-red-500">{error}</div>;

  const totalHours = filteredTimeEntries.reduce(
    (sum, entry) => sum + (parseFloat(entry.hours) || 0),
    0
  );
  const totalExpenses = filteredTimeEntries.reduce(
    (sum, entry) => sum + (parseFloat(entry.expense_amount) || 0),
    0
  );

  const isAllSelected =
    filteredTimeEntries.length > 0 &&
    selectedIds.length === filteredTimeEntries.length;

  return (
    <div className="p-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <h1 className="text-3xl font-bold text-gray-800">
          Time & Expense Entries
        </h1>
        <input
          type="text"
          placeholder="Search by user or description..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="px-4 py-2 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-300 w-full sm:w-72"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <div className="bg-white p-6 rounded-lg shadow-sm">
          <p className="text-gray-500 text-sm">Total Hours</p>
          <p className="text-2xl font-semibold text-gray-900">{totalHours.toFixed(2)}</p>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-sm">
          <p className="text-gray-500 text-sm">Total Expenses</p>
          <p className="text-2xl font-semibold text-gray-900">
            ${totalExpenses.toFixed(2)}
          </p>
        </div>
      </div>

      {/* Bulk actions banner */}
      {canDelete && selectedIds.length > 0 && (
        <div className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 transition-all">
          <div className="flex items-center space-x-2 text-sm text-blue-800 font-medium">
            <span>
              {selectedIds.length} of {filteredTimeEntries.length} selected
            </span>
            <button
              onClick={() => setSelectedIds([])}
              className="text-xs text-blue-600 underline hover:text-blue-800 ml-2"
            >
              Clear Selection
            </button>
          </div>
          <button
            onClick={handleBulkDelete}
            disabled={isDeleting}
            className="flex items-center space-x-1.5 px-3 py-1.5 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-xs font-semibold rounded-md shadow-sm transition-colors cursor-pointer"
          >
            <BiTrash className="w-4 h-4" />
            <span>
              {isDeleting ? "Deleting..." : `Delete Selected (${selectedIds.length})`}
            </span>
          </button>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm overflow-hidden overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {canDelete && (
                <th scope="col" className="px-4 py-3 text-left w-10">
                  <input
                    type="checkbox"
                    checked={isAllSelected}
                    onChange={handleSelectAll}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-pointer"
                  />
                </th>
              )}
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Date
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                User
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Hours
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Expenses
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Description
              </th>
              {canDelete && (
                <th scope="col" className="relative px-6 py-3">
                  <span className="sr-only">Actions</span>
                </th>
              )}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredTimeEntries.length > 0 ? (
              filteredTimeEntries.map((entry) => {
                const isSelected = selectedIds.includes(entry.id);
                return (
                  <tr
                    key={entry.id}
                    className={`hover:bg-gray-50 transition-colors duration-200 ${
                      isSelected ? "bg-blue-50/50" : ""
                    }`}
                  >
                    {canDelete && (
                      <td className="px-4 py-4 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleToggleSelect(entry.id)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-pointer"
                        />
                      </td>
                    )}
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {new Date(entry.entry_date).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {entry.first_name} {entry.last_name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {entry.hours}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      ${parseFloat(entry.expense_amount || 0).toFixed(2)}
                    </td>
                    <td className="px-6 py-4 whitespace-normal text-sm text-gray-500">
                      {entry.description}
                    </td>
                    {canDelete && (
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => handleDelete(entry.id)}
                          className="text-red-600 hover:text-red-900"
                          title="Delete entry"
                        >
                          <BiTrash className="inline-block h-5 w-5" />
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })
            ) : (
              <tr>
                <td
                  colSpan={canDelete ? "7" : "5"}
                  className="px-6 py-4 text-center text-sm text-gray-500"
                >
                  No time entries found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

  export default TimeEntryList;
